import React, { useMemo } from 'react';
import { formatRoute } from 'react-router-named-routes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MealDetailModal from '../meal/meal_detail_modal';
import EndUserFindAlternativesFlattenedBoardView from './end_user_find_alternatives_flattened_board_view';
import { END_USER_FIND_ALTERNATIVES_MULTI_MENUS_WITH_MEAL_DETAIL_ROUTE } from '../../services/routes';
import { linkWithQueryParams } from '../../services/urls';
import { DEFAULT_SPACE_ID } from '../../services/spaces';
import {
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { parentIDOfSharedMealID } from '../../services/smorg_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const showNutritionMetrics = [
  'calories',
  'fat',
  'carbohydrate',
  'protein',
  'fibre',
];

const EndUserFindAlternativesMultiMenusView = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const { calendarDay, mealDetailMealID: showDetailForMealID } = useParams();

  const mealIDToReplace = searchParams.get('mealId');
  const mealType = searchParams.get('mealType');
  const parentIDs = searchParams.getAll('parentIDs');
  const menuIDs = searchParams.getAll('menuIDs');
  console.log({ parentIDs, menuIDs });

  const memoizedParentIDs = useMemo(
    () => parentIDs,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(parentIDs)],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedMenuIDs = useMemo(() => menuIDs, [JSON.stringify(menuIDs)]);

  const menusWithParents = useMemo(
    () =>
      memoizedParentIDs.map((parentID, i) => ({
        parentID,
        menuID: memoizedMenuIDs[i],
      })),
    [memoizedMenuIDs, memoizedParentIDs],
  );

  const onClickResultDetail = (mealID) => {
    const searchParamsArgs = {
      mealType,
      mealId: mealIDToReplace,
      menuIDs,
      parentIDs,
    };
    navigate(
      linkWithQueryParams(
        formatRoute(
          END_USER_FIND_ALTERNATIVES_MULTI_MENUS_WITH_MEAL_DETAIL_ROUTE,
          {
            calendarDay,
            mealDetailMealID: mealID,
          },
        ),
        searchParamsArgs,
      ),
    );
  };

  const onDismissDetail = () => {
    navigate(-1);
  };

  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const parentIDInSharedMeals = parentIDOfSharedMealID(
    showDetailForMealID,
    sharedBoards,
  );

  const { data: distilledSharedMealsByParents } =
    useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
      skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
    });

  const visibleDistilledMealInSharedMeals = ((distilledSharedMealsByParents ||
    {})[parentIDInSharedMeals] || {})[showDetailForMealID];

  const alwaysShowNutrition = !isCommunityUser;

  const { data: fullOwnedMeal, isLoading: isFullOwnedMealLoading } =
    useGetFullMealQuery(showDetailForMealID, {
      skip: visibleDistilledMealInSharedMeals,
    });

  const { data: fullSharedMeal, isLoading: isFullSharedMealLoading } =
    useGetFullSharedMealQuery(showDetailForMealID, {
      skip: !visibleDistilledMealInSharedMeals,
    });

  const fullMeal = visibleDistilledMealInSharedMeals
    ? fullSharedMeal
    : fullOwnedMeal;
  const isFullMealLoading = visibleDistilledMealInSharedMeals
    ? isFullSharedMealLoading
    : isFullOwnedMealLoading;

  const isLoading = isFullMealLoading;

  if (showDetailForMealID && !isLoading && fullMeal) {
    return (
      <MealDetailModal
        fullMeal={fullMeal}
        isLoading={isLoading}
        menu={null}
        isReadOnly
        alwaysShowNutrition={alwaysShowNutrition}
        savingInProgress={false}
        showNutritionMetrics={showNutritionMetrics}
        derivedNutrition={fullMeal?.derivedNutrition}
        canScaleIngredients={false}
        scaleToServings={1}
        currentlyEditingInline={{ section: '', itemId: null }}
        ingredientSuggestions={[]}
        ingredientSuggestionsNetworkState={{ loading: false }}
        onInlineEditFocus={() => {}}
        onInlineEditChange={() => {}}
        onInlineEditBlur={() => {}}
        onAddArraySectionItem={() => {}}
        onRemoveArraySectionItem={() => {}}
        onIngredientCheckChange={() => {}}
        onEditScaleToServings={() => {}}
        onChangeScaleToServings={() => {}}
        resetScaleToServings={() => {}}
        onNewImageChosen={() => {}}
        onRuleChanged={() => {}}
        onAddonChange={() => {}}
        onTagsChange={() => {}}
        onNewImageUrlSet={() => {}}
        onDialogClose={onDismissDetail}
        onUpdateIngredientStructuredData={() => {}}
      />
    );
  }

  return (
    <EndUserFindAlternativesFlattenedBoardView
      calendarDay={calendarDay}
      mealType={mealType}
      mealIDToReplace={mealIDToReplace}
      menusWithParents={menusWithParents}
      onClick={onClickResultDetail}
    />
  );
};

export default EndUserFindAlternativesMultiMenusView;
